import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useFollowTriggerPosition } from '../../hooks/useFollowTriggerPosition';
import { format } from 'date-fns';
import { useI18n } from '../../context/I18nContext';
import { Search, X, Plus } from 'lucide-react';
import Input from './Input';
import Select from './Select';
import DatePicker from './DatePicker';
import Button from './Button';
import { CONTROL_SM } from '../../styles/pageLayout';
import {
  DROPDOWN_MENU_PAPER,
  DROPDOWN_MENU_ITEM,
  DROPDOWN_MENU_ITEM_SELECTED,
  DROPDOWN_MENU_ANIMATION,
  DROPDOWN_MENU_KEYFRAMES,
  DROPDOWN_OPTION_IDLE,
} from './dropdownMenuStyles';

// ---------------------------------------------------------------------------
// Search bar — same height as PageActions / Button sm
// ---------------------------------------------------------------------------

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ value, onChange, placeholder, className = '' }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useI18n();
  const hasValue = (value || '').trim() !== '';

  return (
    <div
      className={`
        group flex items-center min-w-0 w-full max-w-[320px] ${CONTROL_SM}
        rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm
        hover:border-gray-300 dark:hover:border-gray-500
        focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent
        transition-all duration-300 ease-out
        ${className}
      `}
    >
      <span className="pl-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500 shrink-0" aria-hidden>
        <Search className="w-4 h-4" strokeWidth={2} />
      </span>
      <input
        ref={inputRef}
        type="text"
        role="searchbox"
        inputMode="search"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 min-w-0 h-full py-0 pl-2 pr-3 text-base sm:text-sm bg-transparent border-0 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-0"
        aria-label={placeholder}
      />
      {hasValue && (
        <button
          type="button"
          onClick={() => { onChange(''); inputRef.current?.focus(); }}
          className="mr-2 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
          aria-label={t('common.clearFilters')}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Types — clean, maintainable filter model (SaaS 2026)
// ---------------------------------------------------------------------------

/** Column type for filter: text (free text), select (dropdown), number, date. */
export type FilterFieldType = 'text' | 'select' | 'number' | 'date';

/** Definition of a filterable column (id, label, type, options or placeholder). */
export interface FilterFieldDef {
  id: string;
  label: string;
  type: FilterFieldType;
  /** For type 'select': options list. For 'text'/'number'/'date': optional placeholder. */
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export type TextOperator = 'is' | 'is_not' | 'contains' | 'not_contains' | 'is_empty' | 'is_not_empty';
export type SelectOperator = 'is' | 'is_not';

/** One active filter condition (field + operator + value). */
export interface ActiveFilter {
  id: string;
  fieldId: string;
  operator: string;
  value: string;
}

/** Optional search bar config (value + onChange + placeholder). */
export interface SearchConfig {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export interface TableFiltersProps {
  searchConfig?: SearchConfig;
  /** Columns the user can filter by. */
  filterFields: FilterFieldDef[];
  activeFilters: ActiveFilter[];
  onChange: (filters: ActiveFilter[]) => void;
  /**
   * Primary create action shown beside search on mobile only
   * (desktop keeps the button in PageHeader).
   */
  mobilePrimaryAction?: React.ReactNode;
  className?: string;
}

// ---------------------------------------------------------------------------
// Design tokens — same height as Button sm / page actions (CONTROL_SM)
// ---------------------------------------------------------------------------

const CHIP_BASE =
  `${CONTROL_SM} inline-flex items-center rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm ` +
  'hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1';

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1';

/** Add filter button: ring offset 2 so focus ring stays visible on light/dark. */
const ADD_BUTTON_FOCUS =
  'focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900';

const PORTAL_ADD = 'table-filters-add-portal';
const PORTAL_CONFIG = 'table-filters-config-portal';
/** ID of the Select dropdown portal (from Select.tsx) — used to avoid closing when clicking inside. */
const SELECT_DROPDOWN_PORTAL_ID = 'select-dropdown-portal';

/** Popover config width and spacing (positioning). */
const CONFIG_WIDTH = 320;
const SUBMENU_GAP = 4;
const MENU_MARGIN = 8;

/** Generates a unique id for a new active filter. */
function genId(): string {
  return `f-${Math.random().toString(36).slice(2, 11)}`;
}

// Opérateurs selon le type de colonne (string, select, number, date) — source unique pour liste + libellés pills.
const OPERATORS_BY_TYPE: Record<FilterFieldType, { value: string; labelKey: string }[]> = {
  select: [
    { value: 'is', labelKey: 'common.filterOperatorIs' },
    { value: 'is_not', labelKey: 'common.filterOperatorIsNot' },
  ],
  text: [
    { value: 'is', labelKey: 'common.filterOperatorIs' },
    { value: 'is_not', labelKey: 'common.filterOperatorIsNot' },
    { value: 'contains', labelKey: 'common.filterOperatorContains' },
    { value: 'not_contains', labelKey: 'common.filterOperatorNotContains' },
    { value: 'is_empty', labelKey: 'common.filterOperatorIsEmpty' },
    { value: 'is_not_empty', labelKey: 'common.filterOperatorIsNotEmpty' },
  ],
  number: [
    { value: 'is', labelKey: 'common.filterOperatorIs' },
    { value: 'is_not', labelKey: 'common.filterOperatorIsNot' },
    { value: 'greater_than', labelKey: 'common.filterOperatorGreaterThan' },
    { value: 'less_than', labelKey: 'common.filterOperatorLessThan' },
    { value: 'is_empty', labelKey: 'common.filterOperatorIsEmpty' },
    { value: 'is_not_empty', labelKey: 'common.filterOperatorIsNotEmpty' },
  ],
  date: [
    { value: 'is', labelKey: 'common.filterOperatorIs' },
    { value: 'is_not', labelKey: 'common.filterOperatorIsNot' },
    { value: 'before', labelKey: 'common.filterOperatorBefore' },
    { value: 'after', labelKey: 'common.filterOperatorAfter' },
    { value: 'is_empty', labelKey: 'common.filterOperatorIsEmpty' },
    { value: 'is_not_empty', labelKey: 'common.filterOperatorIsNotEmpty' },
  ],
};

/** Returns operator options for a given field type (used in config popover). */
function getOperatorsForType(type: FilterFieldType): { value: string; labelKey: string }[] {
  return OPERATORS_BY_TYPE[type];
}

/** i18n key for operator label in pills (derived from OPERATORS_BY_TYPE). */
const OPERATOR_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  Object.values(OPERATORS_BY_TYPE).flatMap((ops) => ops.map((o) => [o.value, o.labelKey]))
);

/** Whether the operator requires a value input (e.g. "is" yes, "is_empty" no). */
function needsValue(operator: string): boolean {
  return operator !== 'is_empty' && operator !== 'is_not_empty';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TableFilters — Filtres type SaaS (recherche + pills colonne / opérateur / valeur).
 *
 * - SearchBar (optionnel) + pills des filtres actifs + bouton "Ajouter un filtre"
 * - Sur mobile: `mobilePrimaryAction` (ex. Nouveau) à côté de la recherche
 * - Clic "Ajouter" → menu colonnes (portal) ; clic colonne → sous-menu opérateurs + valeur, Annuler / Appliquer en bas
 * - Design : dropdownMenuStyles partagé avec ContextMenu ; tokens CHIP_BASE / FOCUS_RING
 */
const TableFilters: React.FC<TableFiltersProps> = ({
  searchConfig,
  filterFields,
  activeFilters,
  onChange,
  mobilePrimaryAction,
  className = '',
}) => {
  const { t } = useI18n();
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const columnMenuRef = useRef<HTMLDivElement>(null);
  const selectedColumnButtonRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  const [pendingField, setPendingField] = useState<FilterFieldDef | null>(null);
  const [pendingOperator, setPendingOperator] = useState<string>('is');
  const [pendingValue, setPendingValue] = useState('');

  const computeMenuPosition = useCallback((triggerRect: DOMRect) => ({
    top: triggerRect.bottom + 4,
    left: triggerRect.left,
    width: Math.max(triggerRect.width, 200),
  }), []);

  const menuPosition = useFollowTriggerPosition({
    open: menuOpen,
    triggerRef: addButtonRef,
    menuRef: columnMenuRef,
    computePosition: computeMenuPosition,
  });

  const computeConfigPosition = useCallback((triggerRect: DOMRect) => {
    let left = triggerRect.right + SUBMENU_GAP;
    if (left + CONFIG_WIDTH + MENU_MARGIN > window.innerWidth) {
      left = triggerRect.left - CONFIG_WIDTH - SUBMENU_GAP;
      if (left < MENU_MARGIN) left = window.innerWidth - CONFIG_WIDTH - MENU_MARGIN;
    }
    let top = triggerRect.top;
    if (top + 400 + MENU_MARGIN > window.innerHeight) top = window.innerHeight - 400 - MENU_MARGIN;
    if (top < MENU_MARGIN) top = MENU_MARGIN;
    return { top, left, width: CONFIG_WIDTH };
  }, []);

  const configPosition = useFollowTriggerPosition({
    open: configOpen,
    triggerRef: selectedColumnButtonRef,
    computePosition: computeConfigPosition,
  });

  const openColumnMenu = useCallback(() => {
    setMenuOpen(true);
    setConfigOpen(false);
    setPendingField(null);
    setPendingOperator('is');
    setPendingValue('');
  }, []);

  const selectField = useCallback((field: FilterFieldDef, e: React.MouseEvent) => {
    selectedColumnButtonRef.current = e.currentTarget as HTMLButtonElement;
    setPendingField(field);
    setPendingOperator(field.type === 'select' ? 'is' : 'is');
    setPendingValue('');
    setConfigOpen(true);
  }, []);

  const applyFilter = useCallback(() => {
    if (!pendingField) return;
    if (needsValue(pendingOperator) && !pendingValue.trim()) return;
    const newFilter: ActiveFilter = {
      id: genId(),
      fieldId: pendingField.id,
      operator: pendingOperator,
      value: pendingValue.trim(),
    };
    onChange([...activeFilters, newFilter]);
    setConfigOpen(false);
    setMenuOpen(false);
    setPendingField(null);
    setPendingOperator('is');
    setPendingValue('');
  }, [pendingField, pendingOperator, pendingValue, activeFilters, onChange]);

  const removeFilter = useCallback(
    (id: string) => onChange(activeFilters.filter((f) => f.id !== id)),
    [activeFilters, onChange]
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const addEl = document.getElementById(PORTAL_ADD);
      const configEl = document.getElementById(PORTAL_CONFIG);
      const selectDropdownEl = document.getElementById(SELECT_DROPDOWN_PORTAL_ID);
      const insideMenu = addEl?.contains(target);
      const insideConfig = configEl?.contains(target);
      const insideSelectDropdown = selectDropdownEl?.contains(target);
      const onAddButton = addButtonRef.current?.contains(target);
      if (onAddButton) return;
      if (insideMenu || insideConfig || insideSelectDropdown) return;
      setMenuOpen(false);
      setConfigOpen(false);
      setPendingField(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen, configOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setConfigOpen(false);
        setPendingField(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const canApply =
    Boolean(pendingField) &&
    (!needsValue(pendingOperator) || pendingValue.trim() !== '');

  if (filterFields.length === 0 && !searchConfig && !mobilePrimaryAction) return null;

  return (
    <div className={`flex flex-wrap items-center gap-2.5 ${className}`} role="search" aria-label={t('common.filters')}>
      {/* Search (+ mobile create) — same row on phone */}
      {(searchConfig || mobilePrimaryAction) && (
        <div
          className={`flex min-w-0 items-center gap-2 ${
            mobilePrimaryAction ? 'w-full sm:w-auto' : ''
          }`}
        >
          {searchConfig && (
            <SearchBar
              value={searchConfig.value}
              onChange={searchConfig.onChange}
              placeholder={searchConfig.placeholder ?? t('common.search')}
              className={
                mobilePrimaryAction ? 'flex-1 max-w-none sm:max-w-[320px]' : ''
              }
            />
          )}
          {mobilePrimaryAction ? (
            <div className="shrink-0 sm:hidden">{mobilePrimaryAction}</div>
          ) : null}
        </div>
      )}

      {/* Applied filters as pills — same height & style as search */}
      {activeFilters.map((f) => {
        const field = filterFields.find((x) => x.id === f.fieldId);
        if (!field) return null;
        const opLabel = t(OPERATOR_LABEL_KEYS[f.operator] ?? 'common.filterOperatorIs');
        const valueLabel =
          field.type === 'select' && f.value
            ? field.options?.find((o) => o.value === f.value)?.label ?? f.value
            : f.value;
        return (
          <div
            key={f.id}
            className={`inline-flex items-center gap-1 pl-3 pr-1 text-sm leading-none text-gray-700 dark:text-gray-200 whitespace-nowrap ${CHIP_BASE}`}
          >
            <span className="flex items-center gap-1">
              <span>{field.label}</span>
              <span className="text-gray-300 dark:text-gray-600 shrink-0" aria-hidden>·</span>
              <span className="text-gray-400 dark:text-gray-500 shrink-0">{opLabel}</span>
              {needsValue(f.operator) && valueLabel && (
                <>
                  <span className="text-gray-300 dark:text-gray-600 shrink-0" aria-hidden>·</span>
                  <span>{valueLabel}</span>
                </>
              )}
            </span>
            <button
              type="button"
              onClick={() => removeFilter(f.id)}
              className={`p-0.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors shrink-0 flex items-center justify-center ${FOCUS_RING}`}
              aria-label={t('common.clearFilters')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}

      {/* Add filter */}
      {filterFields.length > 0 && (
        <button
          ref={addButtonRef}
          type="button"
          onClick={openColumnMenu}
          className={`inline-flex items-center gap-2 px-3 text-sm font-medium text-gray-700 dark:text-gray-200 ${CHIP_BASE} shrink-0 ${ADD_BUTTON_FOCUS}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen || configOpen}
        >
          <Plus className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
          <span>{t('common.addFilter')}</span>
        </button>
      )}

      {/* Column list — même style que "Plus d'actions" (ContextMenu), reste ancré au scroll */}
      {menuOpen && menuPosition &&
        createPortal(
          <>
            <style>{DROPDOWN_MENU_KEYFRAMES}</style>
            <div
              ref={columnMenuRef}
              id={PORTAL_ADD}
              role="menu"
              className={`fixed z-[9999] ${DROPDOWN_MENU_PAPER}`}
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                minWidth: menuPosition.width,
                animation: DROPDOWN_MENU_ANIMATION,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {filterFields.map((field) => {
                const isSelected = pendingField?.id === field.id && configOpen;
                return (
                  <div key={field.id} className="relative">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={(e) => selectField(field, e)}
                      className={`w-full ${DROPDOWN_MENU_ITEM} ${isSelected ? DROPDOWN_MENU_ITEM_SELECTED : ''} cursor-pointer text-left`}
                    >
                      <span className="flex-1 min-w-0 truncate max-w-[12rem]">{field.label}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </>,
          document.body
        )}

      {/* Operator + value config popover — même famille visuelle, reste ancré au scroll */}
      {configOpen &&
        pendingField &&
        configPosition &&
        createPortal(
          <div
            id={PORTAL_CONFIG}
            className="fixed z-[9999] rounded-surface bg-white/95 dark:bg-white/90 backdrop-blur-md border border-gray-200/60 dark:border-gray-600/60 shadow-paper dark:shadow-paper-dark p-4"
            style={{ top: configPosition.top, left: configPosition.left, width: configPosition.width }}
          >
            {/* 1. Opérateurs ; valeur directement sous l’opérateur sélectionné si besoin */}
            <div className="mb-4">
              <div className="space-y-1.5">
                {getOperatorsForType(pendingField.type).map((op) => {
                  const selected = pendingOperator === op.value;
                  const showValue = selected && needsValue(op.value);
                  const label =
                    op.value === 'is_not_empty'
                      ? t('common.filterOperatorHasAnyValue')
                      : t(op.labelKey);
                  return (
                    <div key={op.value} className="space-y-1.5">
                      <button
                        type="button"
                        onClick={() => setPendingOperator(op.value)}
                        className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left text-sm transition-colors duration-150 ease-out ${
                          selected
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                            : DROPDOWN_OPTION_IDLE
                        } ${FOCUS_RING}`}
                      >
                        <span
                          className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-150 ease-out ${
                            selected
                              ? 'border-primary-500 bg-primary-500'
                              : 'border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700'
                          }`}
                        >
                          <span
                            className={`rounded-full bg-white transition-all duration-150 ease-out ${
                              selected ? 'w-1.5 h-1.5 opacity-100' : 'w-0 h-0 opacity-0'
                            }`}
                          />
                        </span>
                        <span>{label}</span>
                      </button>
                      {showValue && (
                        <div className="pl-6 animate-popover-fade-in">
                          {pendingField.type === 'select' ? (
                            <Select
                              value={pendingValue}
                              onChange={setPendingValue}
                              options={pendingField.options ?? []}
                              placeholder={t('common.allStatus')}
                              showEmptyOptionInList={false}
                            />
                          ) : pendingField.type === 'number' ? (
                            <Input
                              type="number"
                              step="any"
                              value={pendingValue}
                              onChange={(e) => setPendingValue(e.target.value)}
                              placeholder={pendingField.placeholder}
                            />
                          ) : pendingField.type === 'date' ? (
                            <DatePicker
                              value={pendingValue || null}
                              onChange={(date) => setPendingValue(date ? format(date, 'yyyy-MM-dd') : '')}
                              showTimeSelect={false}
                              allowClear
                            />
                          ) : (
                            <Input
                              value={pendingValue}
                              onChange={(e) => setPendingValue(e.target.value)}
                              placeholder={pendingField.placeholder}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Annuler / Appliquer — ghost + primary, aligné avec le reste de l'app */}
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-600">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setConfigOpen(false); setMenuOpen(false); setPendingField(null); }}
              >
                {t('common.filterCancel')}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={applyFilter}
                disabled={!canApply}
              >
                {t('common.filterApply')}
              </Button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TableFilters;
